import React, { useCallback, useEffect, useRef, useState } from 'react';
import { recordScore, getProgress, setLevel, unlockNext, getHighScore } from '../gamesLib';
import { sfx } from '../gameSound';

interface Props {
  onExit: () => void;
  challengeScore?: number | null;
}

type Grid = number[][];
const SIZE = 4;
// Target tile per level — clearing a level unlocks the next.
const TARGETS = [256, 512, 1024, 2048, 4096];

function empty(): Grid {
  return Array.from({ length: SIZE }, () => Array(SIZE).fill(0));
}

function clone(g: Grid): Grid {
  return g.map(r => r.slice());
}

function addRandom(g: Grid): Grid {
  const cells: [number, number][] = [];
  for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) if (g[r][c] === 0) cells.push([r, c]);
  if (!cells.length) return g;
  const [r, c] = cells[Math.floor(Math.random() * cells.length)];
  g[r][c] = Math.random() < 0.9 ? 2 : 4;
  return g;
}

function slideRow(row: number[]): { row: number[]; gained: number } {
  const arr = row.filter(v => v !== 0);
  let gained = 0;
  for (let i = 0; i < arr.length - 1; i++) {
    if (arr[i] === arr[i + 1]) {
      arr[i] *= 2;
      gained += arr[i];
      arr.splice(i + 1, 1);
    }
  }
  while (arr.length < SIZE) arr.push(0);
  return { row: arr, gained };
}

function rotate(g: Grid): Grid {
  const n = empty();
  for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) n[c][SIZE - 1 - r] = g[r][c];
  return n;
}

type Dir = 'left' | 'right' | 'up' | 'down';

function move(g: Grid, dir: Dir): { grid: Grid; gained: number; moved: boolean } {
  let work = clone(g);
  const rotations = dir === 'left' ? 0 : dir === 'up' ? 1 : dir === 'right' ? 2 : 3;
  for (let i = 0; i < rotations; i++) work = rotate(work);
  let gained = 0;
  const next = work.map(row => {
    const res = slideRow(row);
    gained += res.gained;
    return res.row;
  });
  let result = next;
  for (let i = 0; i < (4 - rotations) % 4; i++) result = rotate(result);
  const moved = JSON.stringify(result) !== JSON.stringify(g);
  return { grid: result, gained, moved };
}

function hasMoves(g: Grid): boolean {
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++) {
      if (g[r][c] === 0) return true;
      if (c < SIZE - 1 && g[r][c] === g[r][c + 1]) return true;
      if (r < SIZE - 1 && g[r][c] === g[r + 1][c]) return true;
    }
  return false;
}

const TILE_COLORS: Record<number, string> = {
  2: '#eee4da', 4: '#ede0c8', 8: '#f2b179', 16: '#f59563', 32: '#f67c5f',
  64: '#f65e3b', 128: '#edcf72', 256: '#edcc61', 512: '#edc850',
  1024: '#edc53f', 2048: '#edc22e', 4096: '#3c3a32', 8192: '#3c3a32',
};

const Game2048: React.FC<Props> = ({ onExit, challengeScore }) => {
  const prog = getProgress('2048');
  const [level, setLvl] = useState(prog.level || 1);
  const [grid, setGrid] = useState<Grid>(() => addRandom(addRandom(empty())));
  const [score, setScore] = useState(0);
  const [best] = useState(getHighScore('2048'));
  const [over, setOver] = useState(false);
  const [won, setWon] = useState(false);
  const savedRef = useRef(false);
  const touchRef = useRef<{ x: number; y: number } | null>(null);

  const target = TARGETS[Math.min(level - 1, TARGETS.length - 1)];

  const reset = useCallback((lvl: number) => {
    setGrid(addRandom(addRandom(empty())));
    setScore(0);
    setOver(false);
    setWon(false);
    savedRef.current = false;
    setLvl(lvl);
    setLevel('2048', lvl);
  }, []);

  const doMove = useCallback((dir: Dir) => {
    if (over) return;
    setGrid(prev => {
      const { grid: next, gained, moved } = move(prev, dir);
      if (!moved) return prev;
      addRandom(next);
      if (gained > 0) sfx.pop(); else sfx.move();
      setScore(s => {
        const ns = s + gained;
        return ns;
      });
      const maxTile = Math.max(...next.flat());
      if (maxTile >= target && !won) setWon(true);
      if (!hasMoves(next)) setOver(true);
      return next;
    });
  }, [over, target, won]);

  // Persist score when the game ends (win or lose).
  useEffect(() => {
    if ((over || won) && !savedRef.current) {
      savedRef.current = true;
      recordScore('2048', score, level);
      if (won) { unlockNext('2048', level); sfx.win(); } else sfx.lose();
    }
  }, [over, won, score, level]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const map: Record<string, Dir> = {
        ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down',
        a: 'left', d: 'right', w: 'up', s: 'down',
      };
      const dir = map[e.key];
      if (dir) { e.preventDefault(); doMove(dir); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doMove]);

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touchRef.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 24) return;
    if (Math.abs(dx) > Math.abs(dy)) doMove(dx > 0 ? 'right' : 'left');
    else doMove(dy > 0 ? 'down' : 'up');
    touchRef.current = null;
  };

  return (
    <div className="g2048" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="game-toolbar">
        <button className="game-back" onClick={onExit}>← Arcade</button>
        <div className="game-stats">
          <span className="gstat"><b>{score}</b><i>score</i></span>
          <span className="gstat"><b>{Math.max(best, score)}</b><i>best</i></span>
          <span className="gstat"><b>{target}</b><i>target</i></span>
        </div>
        <button className="game-restart" onClick={() => reset(level)}>↻ New</button>
      </div>

      <div className="g2048-levels">
        {TARGETS.map((t, i) => {
          const lvl = i + 1;
          const unlocked = lvl <= getProgress('2048').unlockedLevel;
          return (
            <button
              key={t}
              className={`lvl-pill ${level === lvl ? 'active' : ''} ${unlocked ? '' : 'locked'}`}
              onClick={() => unlocked && reset(lvl)}
              disabled={!unlocked}
              title={unlocked ? `Level ${lvl} — reach ${t}` : 'Locked — clear the previous level'}
            >
              {unlocked ? `Lvl ${lvl}` : '🔒'}
            </button>
          );
        })}
      </div>

      {challengeScore != null && (
        <div className="challenge-target">🎯 Beat the challenge: <b>{challengeScore}</b> points</div>
      )}

      <div className="g2048-board">
        {grid.map((row, r) => (
          <div key={r} className="g2048-row">
            {row.map((v, c) => (
              <div
                key={c}
                className="g2048-cell"
                style={v ? { background: TILE_COLORS[v] || '#3c3a32', color: v <= 4 ? '#776e65' : '#f9f6f2' } : undefined}
              >
                {v ? <span className={`g2048-tile t${v}`}>{v}</span> : null}
              </div>
            ))}
          </div>
        ))}
      </div>

      {(over || won) && (
        <div className="game-overlay">
          <div className="game-overlay-card">
            <div className="go-emoji">{won ? '🏆' : '💥'}</div>
            <h3>{won ? `Level ${level} cleared!` : 'No more moves'}</h3>
            <p>You scored <b>{score}</b> points.{won && level < TARGETS.length ? ' Next level unlocked.' : ''}</p>
            <div className="go-actions">
              {won && level < TARGETS.length && (
                <button className="g-btn primary" onClick={() => reset(level + 1)}>Next level →</button>
              )}
              <button className="g-btn" onClick={() => reset(level)}>Play again</button>
              <button className="g-btn ghost" onClick={onExit}>Back to arcade</button>
            </div>
          </div>
        </div>
      )}

      <p className="game-hint">Arrow keys / WASD to move · swipe on touch</p>
    </div>
  );
};

export default Game2048;
